import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Bewijs, TaxonomieOverzicht } from '@vip/shared';

import { api } from '../../lib/apiClient';

/**
 * De taxonomie en wat eraan te veranderen valt.
 *
 * `useTaxonomie` stond in `beheer.api.ts`, uit de tijd dat dit scherm alleen
 * toonde en bij de instellingen hoorde. Nu er ook geschreven wordt hoort het
 * bij de eigen feature: de lees- en de schrijfkant van hetzelfde ding horen bij
 * elkaar.
 */
const SLEUTEL = ['taxonomie'];

/** `periode` is "2026-08", "2026" of "alles"; leeg laat de server het jaar
 *  kiezen. Hij hoort bij de sleutel, zodat elke periode zijn eigen antwoord
 *  krijgt en het wisselen niets opnieuw ophaalt. */
export function useTaxonomie(periode?: string) {
  return useQuery({
    queryKey: [...SLEUTEL, periode ?? 'standaard'],
    queryFn: () =>
      api.get<TaxonomieOverzicht>(
        periode ? `/admin/taxonomie?periode=${periode}` : '/admin/taxonomie',
      ),
    // Bij het wisselen van periode blijft de vorige lijst staan tot de nieuwe
    // binnen is. Anders valt de kaart met de kiezer erin even weg, en dan
    // verschuift de knop onder je muis.
    placeholderData: keepPreviousData,
  });
}

/**
 * Het bewijs onder één deelonderwerp. `null` als er geen paneel openstaat, en
 * dan draait de uitvraag niet: dit is de enige plek in de app waar tekst uit
 * een gesprek bij een Admin komt, en die halen we niet vooruit op.
 *
 * Dezelfde periode als de lijst. Zonder dat telt het paneel over alles en de
 * badge over de periode, en dan staan er twee getallen over hetzelfde
 * deelonderwerp op één scherm.
 */
export function useBewijs(deelonderwerpId: string | null, periode?: string) {
  return useQuery({
    queryKey: ['bewijs', deelonderwerpId, periode ?? 'standaard'],
    queryFn: () =>
      api.get<Bewijs>(
        `/admin/taxonomie/deelonderwerpen/${deelonderwerpId}/bewijs${periode ? `?periode=${periode}` : ''}`,
      ),
    enabled: deelonderwerpId !== null,
  });
}

/** Na elke wijziging de hele taxonomie opnieuw, en het geopende bewijs erbij:
 *  samenvoegen verplaatst hulpvragen, dus het paneel klopt anders niet meer. */
function useMutatie<T>(fn: (invoer: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: SLEUTEL });
      await qc.invalidateQueries({ queryKey: ['bewijs'] });
    },
  });
}

export const useHernoemOnderwerp = () =>
  useMutatie(({ id, ...rest }: { id: string; naam: string; bevestigd?: boolean }) =>
    api.patch(`/admin/taxonomie/onderwerpen/${id}`, rest),
  );

export const useWijzigDeelonderwerp = () =>
  useMutatie(
    ({ id, ...rest }: { id: string; naam?: string; onderwerpId?: string; bevestigd?: boolean }) =>
      api.patch(`/admin/taxonomie/deelonderwerpen/${id}`, rest),
  );
